// Validation Engine — persistence + audit (best-effort, non-throwing).
//
// Writes to the append-only audit (validation_events) and the 1:1 latest
// snapshot (strategy_validation). All calls are auth-gated and swallow errors:
// validation persistence must NEVER break the build/backtest UX. See
// supabase/migrations/0075_validation_engine.sql.

import { supabase } from './supabaseClient';
import { logger } from '../utils/logger';
import type { HypothesisCard, RealityCheckVerdict, ValidationStatus } from '../types/validation';

async function currentUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id ?? null;
  } catch {
    return null;
  }
}

// Audit: a hypothesis card was created in the intake.
export async function recordHypothesisCreated(card: HypothesisCard, strategyId?: string): Promise<void> {
  const uid = await currentUserId();
  if (!uid) return;
  try {
    await supabase.from('validation_events').insert({
      author_id: uid,
      strategy_id: strategyId ?? null,
      hypothesis_id: card.id,
      event_type: 'hypothesis_created',
      payload: card as unknown as Record<string, unknown>,
    });
  } catch (e) {
    logger.debug('validation: hypothesis persist failed', e);
  }
}

// Audit: the user dismissed or overrode a rejected-lane match.
export async function recordLaneEvent(
  type: 'lane_dismissed' | 'lane_overridden',
  payload: Record<string, unknown>,
): Promise<void> {
  const uid = await currentUserId();
  if (!uid) return;
  try {
    await supabase.from('validation_events').insert({
      author_id: uid,
      strategy_id: (payload.strategyId as string | undefined) ?? null,
      hypothesis_id: (payload.hypothesisId as string | undefined) ?? null,
      event_type: type,
      payload,
    });
  } catch (e) {
    logger.debug('validation: lane event persist failed', e);
  }
}

// Audit the verdict, and (when a saved strategy id is known) upsert the 1:1
// snapshot that drives the strategy-card status chip.
export async function recordVerdict(
  verdict: RealityCheckVerdict,
  opts: { hypothesisId?: string; strategyId?: string } = {},
): Promise<void> {
  const uid = await currentUserId();
  if (!uid) return;

  const summary = {
    status: verdict.status,
    headline: verdict.headline,
    cost: verdict.cost,
    costAssumptionLabel: verdict.costAssumptionLabel,
    computedAt: verdict.computedAt,
  };

  try {
    await supabase.from('validation_events').insert({
      author_id: uid,
      strategy_id: opts.strategyId ?? null,
      hypothesis_id: opts.hypothesisId ?? null,
      event_type: 'verdict_recorded',
      payload: summary,
    });
  } catch (e) {
    logger.debug('validation: verdict persist failed', e);
  }

  if (opts.strategyId) {
    try {
      await supabase.from('strategy_validation').upsert(
        {
          strategy_id: opts.strategyId,
          author_id: uid,
          status: verdict.status,
          verdict: summary,
          cost_assumption: { label: verdict.costAssumptionLabel },
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'strategy_id,author_id' },
      );
    } catch (e) {
      logger.debug('validation: snapshot upsert failed', e);
    }
  }
}

// Read latest statuses for a set of strategy ids (drives the card chips).
export async function getStrategyValidationStatuses(
  ids: string[],
): Promise<Record<string, ValidationStatus>> {
  const out: Record<string, ValidationStatus> = {};
  if (!ids.length) return out;
  try {
    const { data } = await supabase
      .from('strategy_validation')
      .select('strategy_id,status')
      .in('strategy_id', ids);
    for (const r of (data ?? []) as Array<{ strategy_id: string; status: ValidationStatus }>) {
      out[r.strategy_id] = r.status;
    }
  } catch (e) {
    logger.debug('validation: status fetch failed', e);
  }
  return out;
}
